<template>
  <div class="card modal-overflow edit-job" style="box-shadow: none;">
    <div class="card-body new-job">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>Job Name</label>
            <div class="job-name-container grey-font">
              <h6>
                {{ ptracJob.name }}
                <span v-if="ptracJob.actualCompletionDate">
                  - Completed on: {{ ptracJob.actualCompletionDate | formatDate }}
                </span>
              </h6>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>
              Job Number
            </label>
            <el-input
              size="large"
              name="jobNumber"
              :disabled="true"
              class="job-number"
              v-model="ptracJob.number"
              placeholder="Job Number">
            </el-input>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Target Completion Date</label>
            <el-date-picker
              :disabled="true"
              name="targetCompletionDate"
              :format="constants.DATE.FORMAT.DISPLAY"
              :value-format="constants.DATE.FORMAT.VALUE"
              v-model="ptracJob.targetCompletionDate"
              :picker-options="datePickerConfig"
              type="date">
            </el-date-picker>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-3">
          <span>Requested Status</span>
          <div class="card" style="padding:10px; margin:2px;">
            Cancelled
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label>Job Status</label>
            <el-select class="select"
                       name="status"
                       size="large"
                       :clearable=false
                       @change="warnWhenCompletedOrCancelled"
                       :disabled="!canEdit"
                       placeholder="Job Status"
                       v-validate="modelValidations.status"
                       v-model="ptracJob.status">
              <el-option v-for="option in statuses"
                         class="select"
                         :value="option.value"
                         :label="option.label"
                         :key="option.label">
              </el-option>
            </el-select>
            <small class="text-danger" v-show="errors.has('status')">
              {{ getError('status') }}
            </small>
            <small v-if="ptracJob.status !== constants.STATUSES.CANCELLED" class="text-danger">
              Status not set to "Cancelled"!
            </small>
          </div>
        </div>
        <div class="col-md-6">
          <auditable-entity :data="ptracJob"></auditable-entity>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <button @click="validateAndSubmit"
                    class="btn btn-fill btn-info btn-wd btn-save pull-right"
                    v-if="canEdit"
                    :disabled="changesSaved">Save Job</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {notifyError, notifySuccess, utcToLocal,} from 'src/utilities/utils'
import AuditableEntity from 'src/components/UIComponents/AuditableEntity'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-editor-classic/src/classiceditor'
import ckEditorConfig from 'src/utilities/ckeditor'
import {OK} from 'http-status-codes'
import _ from 'lodash'
import {sync} from 'vuex-pathify'
import {jobWarnWhenCompletedOrCancelled} from '@/utilities/jobHelper'

export default {
    props: ['jobId', 'purchaseOrder'],
    components: {
      AuditableEntity,
      ckeditor: CKEditor.component
    },
    computed: {
      ptracJob: sync('supplyPro/job/ptracJob'),
      statuses() {
        const allStatuses = this.$store.getters['statuses/job']
        // if job has been cancelled or completed, do not allow a further status change
        if (this.initialJobStatus === Constants.STATUSES.CANCELLED
          || this.initialJobStatus === Constants.STATUSES.COMPLETED) {
          return _.filter(allStatuses, (status) => status.value === this.initialJobStatus)
        }
        return _.filter(allStatuses, (status) => status.value === this.initialJobStatus || status.value === Constants.STATUSES.CANCELLED)
      },
      canEdit() {
        if (this.$store.get('security/isInGroup', Constants.GROUPS.SUPER_USERS)) {
          return true
        }
        if (this.$store.get('supplyPro/job/ptracJob@actualCompletionDate')) {
          return false
        }
        return this.$can('edit', Permissions.JOBS)
      },
      requestedChangeMade() {
        return (this.$store.get('supplyPro/job/ptracJob@status') === this.constants.STATUSES.CANCELLED)
      },
      requestedChangeSaved() {
        return (this.requestedChangeMade && this.changesSaved)
      }
    },
    data() {
      return {
        editor: ClassicEditor,
        datePickerConfig,
        ckEditorConfig,
        changesSaved: true,
        constants: Constants,
        initialJobStatus: null,
        modelValidations: {
          status: {
            required: true
          }
        }
      }
    },
    methods: {
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      validate() {
        return this.$validator.validateAll()
      },
      getLabel(obj) {
        if (obj != null && obj.hasOwnProperty('name')) {
          return obj.name
        }
        return obj
      },
      warnWhenCompletedOrCancelled(jobStatus) {
        jobWarnWhenCompletedOrCancelled(this, jobStatus)
        this.setChangesSaved(false)
      },
      setChangesSaved(bool) {
        this.changesSaved = bool
      },
      setJobStatus(jobStatus) {
        this.$store.set('supplyPro/job/ptracJob@status', jobStatus)
      },
      async validateAndSubmit() {
        if (await this.$validator.validateAll()) {
          const savePayload = this.$store.get('supplyPro/job/savePayload')
          const response = await this.$store.dispatch('save', savePayload)

          if (response.result === Constants.RETURN_CODES.SUCCESS) {
            notifySuccess(this, 'Job saved successfully')
            this.setChangesSaved(true)
            this.$eventHub.$emit('jobSaved')
          } else {
            notifyError(this, 'Failed to Update Job', response.data.errors[0])
          }
        }
      },
    },
    watch: {
      requestedChangeSaved(newValue) {
          this.$emit('requestedJobChanged', newValue)
      },
    },
    async mounted() {
      await this.$store.cache.dispatch('statuses/fetchByEntityType', 'Job')

      const response = await this.$store.dispatch('fetchById', {endpoint: 'jobs', id: this.jobId})
      if (OK !== response.status) {
        notifyError(this, 'Failed to load Job by Id: ' + this.jobId)
        return
      }
      const job = response.data

      // dates coming from ptrac-api are a GMT date string
      job.dateReceived = utcToLocal(job.dateReceived)
      job.targetCompletionDate = utcToLocal(job.targetCompletionDate)

      this.$store.set('supplyPro/job/ptracJob', job)
      this.initialJobStatus = job.status

      this.$eventHub.$on('resetJobStatus', this.setJobStatus)
    },
    beforeDestroy() {
      this.$store.commit('supplyPro/job/reset')

      this.$eventHub.$off('resetJobStatus')
    },

  }
</script>

<style scoped>
  .edit-job >>> #autocomplete-client .v-autocomplete-list, #autocomplete-builder .v-autocomplete-list {
    min-width: 280px;
    margin-top: 12px;
  }
  .edit-job >>> .job-name-container {
    background-color: lightgrey;
    padding: 11px 0 4px 8px;
    border-radius: 5px;
  }
  .edit-job >>> .tiny-label-button {
    padding: 1px 5px;
    margin-top: -1px;
    float: right;
  }
</style>
